import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  withTextLocalizer,
  encryptCreditCardData,
  addPayment,
  getPaymentPublicKey,
  getPaymentData,
  selectPaymentPublicKey,
  selectActivePaymentData,
  selectUser,
  selectNetworkActivities,
  CLIENT_TYPE,
  LocalizePropType,
  getUser,
  getNextUserOpenChallengeRoute,
  selectBrandSettings,
  checkoutAddPayment,
  addPaymentDetails,
  clearDialog,
  getPaymentMethods,
} from 'getaway-data-layer';
import { useFormik } from 'formik';
import { differenceInMinutes } from 'date-fns';
import {
  GA_TITLES,
  getAdyenCheckoutConfigs,
  navigateTo,
  parseDropinPaymentMethods,
  reportGAEvent,
} from '../../../../../utils';
import AddCCPaymentView from './AddCCPaymentView';
import { AddCCModal, AddCCSchema } from '../../../../../validation';
import { AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';

type InputItem = {
  value: string;
  error: boolean;
};

type Values = {
  name: string;
};

const initialInput = {
  value: '',
  error: false,
};

type Props = {
  localize: LocalizePropType;
};

const PAYMENT_UPDATE_PERIOD = 5000;

const AddCCPaymentContainer = ({ localize }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const user: any = useSelector(selectUser);
  const paymentPublicKey = useSelector(selectPaymentPublicKey);
  const activePayment: any = useSelector(selectActivePaymentData);
  const networkActivities: any = useSelector(selectNetworkActivities);
  const isAddingPayment = networkActivities.includes(
    CLIENT_TYPE.USER_CLIENT.ADD_PAYMENT
  );
  const brandSettings = useSelector(selectBrandSettings);
  const useDropInComponent = brandSettings?.useDropInComponent;

  const paymentReqId = useRef(
    user?.paymentMethods.length > 0 &&
      user?.paymentMethods[0]?.paymentRequestId &&
      differenceInMinutes(new Date(), new Date(activePayment?.created)) < 5
      ? user?.paymentMethods[0]?.paymentRequestId
      : null
  );

  const [cardNumber, setCardNumber] = useState<InputItem>(initialInput);
  const [cardExpiry, setCardExpiry] = useState<InputItem>(initialInput);
  const [cardCVC, setCardCVC] = useState<InputItem>(initialInput);

  const onChangeNumber = (e: any) => {
    setCardNumber({ value: e.target.value, error: false });
  };

  const onChangeExpiry = (e: any) => {
    setCardExpiry({ value: e.target.value, error: false });
  };

  const onChangeCVC = (e: any) => {
    setCardCVC({ value: e.target.value, error: false });
  };

  const onNumberError = (error: string) => {
    setCardNumber({ ...cardNumber, error: Boolean(error) });
  };

  const onExpiryError = (error: string) => {
    setCardExpiry({ ...cardExpiry, error: Boolean(error) });
  };

  const onCVCError = (error: string) => {
    setCardCVC({ ...cardCVC, error: Boolean(error) });
  };

  const validateInput = (inputItem: InputItem) =>
    inputItem.value && !inputItem.error;

  const getPublicKey = async () => {
    let key = paymentPublicKey;
    if (key && key?.length > 0) return new Promise((resolve) => resolve(key));
    await dispatch(
      getPaymentPublicKey((publicKey: string) => {
        key = publicKey;
      })
    );
    return new Promise((resolve, reject) =>
      key && key.length > 0 ? resolve(key) : reject()
    );
  };

  const encryptCardData = (name: string) =>
    // @ts-ignore
    getPublicKey().then((key: string) => {
      return encryptCreditCardData(
        key,
        cardNumber.value,
        cardExpiry.value,
        cardCVC.value,
        name
      );
    });

  const successCallback = (updatedUser) => {
    navigateTo(history, getNextUserOpenChallengeRoute(updatedUser), false);
  };

  const onSubmit = (values: Values) => {
    if (
      validateInput(cardNumber) &&
      validateInput(cardExpiry) &&
      validateInput(cardCVC)
    ) {
      encryptCardData(values.name).then((data: any) => {
        if (data) {
          dispatch(
            addPayment(
              {
                card: {
                  cardEncryptedJson: data,
                  accountHolder: values.name.trim(),
                },
              },
              (userData: any) => {
                if (userData?.paymentMethods[0]?.paymentRequestId) {
                  paymentReqId.current =
                    userData?.paymentMethods[0]?.paymentRequestId;
                }
                // navigate if cc with no 3ds added
                if (userData?.paymentMethods[0]?.status === 'verified') {
                  successCallback(userData);
                }

                reportGAEvent(GA_TITLES.ON_BOARDING.CC_SUBMITTED);
              }
            )
          );
        }
      });
    }
  };

  const onClose = () => {
    history.goBack();
  };

  const AddCCFormik = useFormik({
    initialValues: AddCCModal,
    validationSchema: AddCCSchema(localize),
    onSubmit,
  });

  useEffect(() => {
    const paymentDataUpdaterId = setInterval(() => {
      if (!useDropInComponent && paymentReqId.current) {
        dispatch(getPaymentData(paymentReqId.current));
        dispatch(
          getUser((user) => {
            // @ts-ignore
            if (user?.paymentMethods[0]?.status === 'verified') {
              successCallback(user);
            }
          })
        );
      }
    }, PAYMENT_UPDATE_PERIOD);
    return () => {
      if (paymentDataUpdaterId) {
        clearInterval(paymentDataUpdaterId);
      }
    };
  }, []);

  useEffect(() => {
    if (
      !useDropInComponent &&
      activePayment?.status === 'confirmed' &&
      paymentReqId.current
    ) {
      successCallback(user);
    }
  }, [activePayment?.status]);

  const onSubmitAdyenCardPayment = (state, component, actions) => {
    // submit CC
    if (state?.data?.paymentMethod?.holderName) {
      dispatch(
        checkoutAddPayment(
          {
            card: {
              ...state.data.paymentMethod,
              returnUrl: document.location.href,
            },
          },
          (data: any) => {
            if (data?.action) {
              component.handleAction(data.action);
            }
            if (actions?.resolve) {
              actions.resolve();
            }
            successCallback(data);
            reportGAEvent(GA_TITLES.ON_BOARDING.CC_SUBMITTED);
          }
        )
      );
    }
  };

  const onSubmitAdyenCardPaymentDetails = (state, component, actions) => {
    dispatch(
      addPaymentDetails(state.data, (response) => {
        if (response.action) {
          // Drop-in handles the action object from the /payments response
          component.handleAction(response.action);
          actions.resolve();
        } else {
          // Your function to show the final result to the shopper
          if (component) component.setStatus('success');
          dispatch(clearDialog());
          successCallback(response);
          if (actions) actions.resolve();
        }
      })
    );
  };

  const initializeAdyen = async (paymentMethods: any) => {
    // Create an instance of AdyenCheckout using the configuration object.
    // @ts-ignore
    const checkout = await AdyenCheckout(
      getAdyenCheckoutConfigs(
        user?.locale,
        user.address?.country || brandSettings?.defaultCountryCode,
        brandSettings?.adyenClientKey!,
        paymentMethods,
        onSubmitAdyenCardPayment,
        onSubmitAdyenCardPaymentDetails
      )
    );

    // Create the dropin configuration with paymentMethodComponents
    const dropinConfiguration = {
      // paymentMethodComponents: [Card],
      // paymentMethodsConfiguration: {
      //   card: {
      hasHolderName: true,
      holderNameRequired: true,
      enableStoreDetails: true,
      hideCVC: false,
      name: 'Credit or debit card',
      // },
      // },
    };

    // Use the constructor pattern as shown in the documentation
    const dropInComponent = new Card(checkout, dropinConfiguration);
    dropInComponent.mount('#card-div');
  };

  const location = useLocation();

  const handleRedirect = async () => {
    // Extract the values from the query string parameters
    const queryParams = new URLSearchParams(location.search);
    // const sessionId = queryParams.get('sessionId');
    const redirectResult = queryParams.get('redirectResult');

    // If we have both sessionId and redirectResult, handle the redirect
    if (redirectResult) {
      // Create an instance of AdyenCheckout using the sessionId
      // This follows the Adyen documentation for handling redirects
      dispatch(
        getPaymentMethods(async (paymentMethods) => {
          const checkout = await AdyenCheckout({
            ...getAdyenCheckoutConfigs(
              user?.locale,
              user.address?.country || brandSettings?.defaultCountryCode,
              brandSettings?.adyenClientKey!,
              paymentMethods,
              onSubmitAdyenCardPayment,
              onSubmitAdyenCardPaymentDetails
            ),
            onPaymentCompleted: (result, component) => {
              if (component) component.setStatus('success');
              dispatch(clearDialog());
              successCallback(user);
              reportGAEvent(GA_TITLES.ON_BOARDING.CC_SUBMITTED);
            },
            onError: (error, component) => {
              console.error('Payment error:', error);
              if (component) component?.setStatus('error');
            },
          });
          const dropinConfiguration = {
            // paymentMethodComponents: [Card],
            // paymentMethodsConfiguration: {
            // card: {
            hasHolderName: true,
            holderNameRequired: true,
            enableStoreDetails: true,
            hideCVC: false,
            name: 'Credit or debit card',
            // },
            // },
          };

          const dropInComponent = new Card(checkout, dropinConfiguration);
          dropInComponent.mount('#card-div');
          // Submit the redirectResult - this is the key step from the Adyen docs
          checkout.submitDetails({ details: { redirectResult } });
        })
      );
    } else {
      // Normal initialization if not handling a redirect
      dispatch(
        getPaymentMethods((paymentMethods) => {
          initializeAdyen(parseDropinPaymentMethods(paymentMethods));
        })
      );
    }
  };

  useEffect(() => {
    if (useDropInComponent) {
      setTimeout(() => {
        handleRedirect();
      }, 50);
    }
  }, []);
  return (
    <>
      <AddCCPaymentView
        localize={localize}
        formik={AddCCFormik}
        cardNumber={cardNumber.value}
        cardExpiry={cardExpiry.value}
        cardCVC={cardCVC.value}
        redirectData={activePayment?.paymentData?.threeDS1}
        identifyShopperData={activePayment?.paymentData?.identifyShopperData}
        challengeShopperData={activePayment?.paymentData?.challengeShopperData}
        isAddingPayment={isAddingPayment}
        onChangeNumber={onChangeNumber}
        onChangeExpiry={onChangeExpiry}
        onChangeCVC={onChangeCVC}
        onClose={onClose}
        onNumberError={onNumberError}
        onExpiryError={onExpiryError}
        onCVCError={onCVCError}
        useDropInComponent={useDropInComponent}
      />
    </>
  );
};

export default withTextLocalizer(AddCCPaymentContainer);
